import { HbrDatePicker } from '@harbor/elements-react-standalone';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  useFetchOpportunityProductAssignment,
  useUpdateOpportunityProductAssignmentMutation,
} from '../../../../../hooks/opportunity-hooks';
import { OpportunityPoV } from '../../../../../types/opportunity-pov-items';
import { asSingleValue } from '../../../../../support/utils';

interface DatesSectionProps {
  pov: OpportunityPoV;
  selectedProductId: number | null;
  onValidationChange: (isValid: boolean) => void;
  onLoadingChange: (isLoading: boolean) => void;
}

const style = {
  control: 'w-80 max-w-full min-h-20',
  heading: 'py-4 text-xl flex',
};

const DatesSection: React.FC<DatesSectionProps> = ({
  pov,
  selectedProductId,
  onValidationChange,
  onLoadingChange,
}) => {
  const { id: opportunityId } = useParams();

  // Fetch product assignment data
  const { data: selectedProduct, isLoading: loadingProduct } = useFetchOpportunityProductAssignment(
    selectedProductId,
    opportunityId,
    { enabled: !!selectedProductId && !!opportunityId }
  );

  // Mutation for updating dates
  const { mutateAsync: updateOpportunityProduct, isPending: isUpdatingOpportunityProduct } =
    useUpdateOpportunityProductAssignmentMutation();

  const isLoading = loadingProduct || isUpdatingOpportunityProduct;

  // Form state
  const isFormDirtyRef = useRef(false);
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const hasInitialized = useRef(false);
  const lastPovId = useRef<number | null>(null);
  const lastProductId = useRef<number | null>(null);

  // Work around to determine if a user input has made form dirty and not initialization
  const isFocused = useRef({
    startDate: false,
    endDate: false,
  });

  // Report loading state to parent
  useEffect(() => {
    onLoadingChange(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const {
    control,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      startDate: null as Date | null,
      endDate: null as Date | null,
      seManagerApproved: false,
    },
  });

  // Validate and report to parent whenever form might have changed
  const validateDates = () => {
    const startDate = getValues('startDate');
    const endDate = getValues('endDate');
    const isValid = !!(startDate && endDate);
    onValidationChange(isValid);
  };

  // Reset form when product data loads or changes
  useEffect(() => {
    const povIdChanged = pov?.id !== lastPovId.current;
    const productIdChanged = selectedProductId !== lastProductId.current;

    if (!isLoading && selectedProduct && (povIdChanged || productIdChanged || !hasInitialized.current)) {
      reset({
        startDate:
          selectedProduct?.planned_start_date && selectedProduct.planned_start_date !== '1999-01-01'
            ? new Date(selectedProduct?.planned_start_date)
            : new Date(),
        endDate:
          selectedProduct?.planned_end_date && selectedProduct.planned_end_date !== '1999-01-01'
            ? new Date(selectedProduct?.planned_end_date)
            : new Date(),
        seManagerApproved: selectedProduct?.sem_approved ?? false,
      });

      hasInitialized.current = true;
      lastPovId.current = pov?.id ?? null;
      lastProductId.current = selectedProductId;
      isFormDirtyRef.current = false;
      validateDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.id, isLoading, pov?.id, selectedProductId]);

  // Save dates when form is updated and dirty
  useEffect(() => {
    const saveDates = async () => {
      if (selectedProduct) {
        const formatter = new Intl.DateTimeFormat('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });

        try {
          isFormDirtyRef.current = false;
          await updateOpportunityProduct({
            ...selectedProduct,
            product_id: selectedProduct?.product?.id,
            sem_approved: getValues('seManagerApproved'),
            planned_start_date: formatter.format(new Date(getValues('startDate')!)),
            planned_end_date: formatter.format(new Date(getValues('endDate')!)),
          });
        } catch (error) {
          console.error('Error updating opportunity product dates:', error);
        }
      }
    };

    if (isFormUpdated && isFormDirtyRef.current) {
      saveDates();
      setIsFormUpdated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormUpdated]);

  return (
    <div>
      <div className={style.heading}>
        <div>Planned Dates</div>
      </div>
      <div className="relative lg:mr-4">
        <div className={style.control}>
          <Controller
            name="startDate"
            control={control}
            render={({ field: { ref, value, ...field } }) => (
              <HbrDatePicker
                {...field}
                value={(value instanceof Date && !isNaN(value.getTime()) ? new Intl.DateTimeFormat('en-CA').format(value) : '') as any}
                label="Planned Start Date"
                showDaysInNextAndPreviousMonths={true}
                calendarStyle="square"
                onHbr-focus={() => (isFocused.current.startDate = true)}
                onHbr-change={(e) => {
                  // Only process user-initiated changes (after focus)
                  if (!isFocused.current.startDate) {
                    return;
                  }

                  const rawValue = e.detail.value;
                  isFocused.current.startDate = false;

                  if (!rawValue) {
                    field.onChange(null);
                    setTimeout(() => validateDates(), 0);
                    return;
                  }
                  
                  const oldValue = new Date(getValues('startDate')!).getTime();
                  const newValue = new Date(asSingleValue(rawValue)).getTime();
                  
                  if (oldValue !== newValue) {
                    isFormDirtyRef.current = true;
                    const newDate = new Date(asSingleValue(rawValue));
                    field.onChange(newDate);
                    setIsFormUpdated(true);
                    // Validate after change
                    setTimeout(() => validateDates(), 0);
                  }
                }}
              />
            )}
          />
        </div>
        <div className={style.control}>
          <Controller
            name="endDate"
            control={control}
            render={({ field: { ref, value, ...field } }) => (
              <HbrDatePicker
                {...field}
                value={(value instanceof Date && !isNaN(value.getTime()) ? new Intl.DateTimeFormat('en-CA').format(value) : '') as any}
                label="Planned End Date"
                showDaysInNextAndPreviousMonths={true}
                calendarStyle="square"
                onHbr-focus={() => (isFocused.current.endDate = true)}
                onHbr-change={(e) => {
                  // Only process user-initiated changes (after focus)
                  if (!isFocused.current.endDate) {
                    return;
                  }

                  const rawValue = e.detail.value;
                  isFocused.current.endDate = false;

                  if (!rawValue) {
                    field.onChange(null);
                    setTimeout(() => validateDates(), 0);
                    return;
                  }
                  
                  const oldValue = new Date(getValues('endDate')!).getTime();
                  const newValue = new Date(asSingleValue(rawValue)).getTime();
                  
                  if (oldValue !== newValue) {
                    isFormDirtyRef.current = true;
                    const newDate = new Date(asSingleValue(rawValue));
                    field.onChange(newDate);
                    setIsFormUpdated(true);
                    // Validate after change
                    setTimeout(() => validateDates(), 0);
                  }
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default DatesSection;
